import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL, BOOKING_URL } from "@/lib/constants";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "The Situation", href: "#situation" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Urgency", href: "#urgency" },
  { label: "Contact", href: "#lead-form" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#040b16]/85 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
        <a
          data-testid="nav-logo"
          href="#top"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="Tariff Refund Agency"
            className="h-11 w-auto object-contain"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="font-mono uppercase tracking-[0.18em] text-[11px] text-white/70 hover:text-[#F5B800] transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 group-hover:w-full h-px bg-[#F5B800] transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-book-assessment"
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex btn-magnetic items-center gap-2 bg-[#F5B800] hover:bg-[#DDA600] text-[#0A1936] font-semibold text-sm px-5 py-3 rounded-sm transition-colors"
          >
            Book Assessment
            <span aria-hidden>→</span>
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#040b16]/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-mono uppercase tracking-[0.18em] text-xs text-white/80 hover:text-[#F5B800]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="mt-3 bg-[#F5B800] text-[#0A1936] font-semibold text-sm px-5 py-3 rounded-sm text-center"
              >
                Book Assessment →
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
